// src/services/privateChatPresenceSignalRService.ts

import * as signalR from "@microsoft/signalr";
import { CONFIG } from "../config";

export type PrivateChatPresencePayload = {
  type?:
    | "presence_changed"
    | "typing_changed"
    | "message_created"
    | "private_chat_seen"
    | "updatedChat"
    | "chatDeleted"
    | "chatTransferred";
  chatId?: string;
  userId?: string;
  inChat?: boolean;
  typing?: boolean;
  chat?: any;
  text?: string;
  value?: string;
  timestamp?: number;
};

type NegotiateResponse = {
  url: string;
  accessToken: string;
};

let presenceConnection: signalR.HubConnection | null = null;
let connectingPromise: Promise<signalR.HubConnection> | null = null;
const realtimeListeners = new Set<
  (payload: PrivateChatPresencePayload) => void
>();

function getPresenceApiBase() {
  // IMPORTANT:
  // This must be the same backend as iOS:
  // https://idlessmicro1-c9eye5d8e6effggj.westeurope-01.azurewebsites.net/api
  return CONFIG.apiUrlSecondary;
}

export async function establishPrivateChatPresenceConnection(
  userId: string,
  accessToken: string,
  onRealtimeEvent: (payload: PrivateChatPresencePayload) => void,
): Promise<signalR.HubConnection> {
  realtimeListeners.add(onRealtimeEvent);

  if (
    presenceConnection &&
    presenceConnection.state === signalR.HubConnectionState.Connected
  ) {
    console.log("✅ Web presence SignalR already connected");

    return presenceConnection;
  }

  if (connectingPromise) {
    console.log("⏳ Web presence SignalR connection already in progress");

    const existing = await connectingPromise;

    return existing;
  }

  const encodedToken = encodeURIComponent(accessToken);

  const negotiateUrl =
    `${getPresenceApiBase()}/private-chat/realtime/${userId}/negotiate` +
    `?access_token=${encodedToken}`;

  console.log("🔌 Web presence negotiate URL:", negotiateUrl);

  connectingPromise = (async () => {
    const negotiateResponse = await fetch(negotiateUrl, {
      method: "POST",
    });

    console.log(
      "🔌 Web presence negotiate status:",
      negotiateResponse.status,
      negotiateResponse.statusText,
    );

    if (!negotiateResponse.ok) {
      const text = await negotiateResponse.text().catch(() => "");
      throw new Error(
        `Presence negotiate failed ${negotiateResponse.status}: ${text}`,
      );
    }

    const negotiated: NegotiateResponse = await negotiateResponse.json();

    console.log("🔌 Web presence negotiated url:", negotiated.url);

    const conn = new signalR.HubConnectionBuilder()
      .withUrl(negotiated.url, {
        accessTokenFactory: () => negotiated.accessToken,
        transport: signalR.HttpTransportType.WebSockets,
      })
      .withAutomaticReconnect()
      .build();

    conn.off("privateChatRealtime");
    conn.on("privateChatRealtime", (payload: PrivateChatPresencePayload) => {
      realtimeListeners.forEach((listener) => {
        listener(payload);
      });
    });

    conn.onreconnecting((error) => {
      console.warn("⚠️ Web presence SignalR reconnecting", error);
    });

    conn.onreconnected(() => {
      console.log("✅ Web presence SignalR reconnected");
    });

    conn.onclose((error) => {
      console.warn("🔌 Web presence SignalR closed", error);
      presenceConnection = null;
      connectingPromise = null;
    });

    await conn.start();

    console.log("✅ Web presence SignalR connected");

    presenceConnection = conn;
    connectingPromise = null;

    return conn;
  })();

  return connectingPromise;
}

export function removePrivateChatPresenceListener(
  onRealtimeEvent: (payload: PrivateChatPresencePayload) => void,
) {
  realtimeListeners.delete(onRealtimeEvent);
}

export async function closePrivateChatPresenceConnection() {
  if (!presenceConnection) return;

  try {
    await presenceConnection.stop();
    console.log("🔌 Web presence SignalR stopped");
  } catch (error) {
    console.error("Failed to stop web presence SignalR", error);
  } finally {
    presenceConnection = null;
    connectingPromise = null;
  }
}
